import { useCallback, useEffect, useMemo, useState } from "react";

import { createSelector } from "@reduxjs/toolkit";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import { useScheduleExperience } from "features/scheduling/hooks/use-schedule-experience.hook";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

const clinicFiltersStorage = setupLocalStorageAPI("clinic-filters");

const getFilters = () => clinicFiltersStorage.getData({});
const getCacheFilters = (cacheKey: string | null) =>
  cacheKey ? (getFilters()?.[cacheKey] ?? null) : null;

const selectStorageData = createSelector(
  [
    selectCurrentClinic,
    selectCurrentRegion,
    selectCurrentUser,
    selectHasRegions
  ],
  (currentClinic, currentRegion, currentUser, hasRegions) => ({
    currentClinic,
    currentRegion,
    currentUser,
    hasRegions
  })
);

export default function useStorage(disabled?: boolean) {
  const { currentClinic, currentRegion, currentUser, hasRegions } =
    useAppSelector(selectStorageData);
  const { isSchedule21Enabled } = useScheduleExperience();

  const { fullKey } = useMemo(() => {
    if (disabled) return { savePerRegion: false, cacheKey: null, fullKey: null };
    const savePerRegion =
      !isSchedule21Enabled &&
      !Boolean(currentUser?.scheduler_persist_per_clinic) &&
      hasRegions;
    const cacheKey = savePerRegion ? currentRegion?.id : currentClinic?.id;
    const fullKey = cacheKey
      ? `${savePerRegion ? "region" : "clinic"}:${cacheKey}`
      : null;
    return { savePerRegion, cacheKey, fullKey };
  }, [
    disabled,
    isSchedule21Enabled,
    currentUser?.scheduler_persist_per_clinic,
    hasRegions,
    currentRegion?.id,
    currentClinic?.id
  ]);

  // Lazy migration: when 2.1 activates for a region-based practice, copy the
  // existing region:<id> filter entry to clinic:<id> so users don't lose their
  // saved filters on first 2.1 load. Runs per-clinic so A→B→A restores each.
  // Idempotent: skips if clinic:<id> already has data.
  useEffect(() => {
    if (!isSchedule21Enabled || disabled) return;
    const wouldHaveBeenRegionKeyed =
      !Boolean(currentUser?.scheduler_persist_per_clinic) && hasRegions;
    if (!wouldHaveBeenRegionKeyed || !currentRegion?.id || !currentClinic?.id)
      return;
    const regionKey = `region:${currentRegion.id}`;
    const clinicKey = `clinic:${currentClinic.id}`;
    const filters = getFilters();
    if (filters[clinicKey] !== undefined || !filters[regionKey]) return;
    clinicFiltersStorage.updateData({ ...filters, [clinicKey]: filters[regionKey] });
    setStorageVersion((v) => v + 1);
  }, [
    isSchedule21Enabled,
    disabled,
    currentUser?.scheduler_persist_per_clinic,
    hasRegions,
    currentRegion?.id,
    currentClinic?.id
  ]);

  // Incremented on each updateStorage call to invalidate the useMemo below,
  // ensuring we re-read localStorage after writes (fixes stale defaultStaff
  // when resetToDefaults persists new values within the same fullKey).
  const [storageVersion, setStorageVersion] = useState(0);

  const { defaultClinics, defaultStaff, defaultView } = useMemo(() => {
    if (disabled) {
      return { defaultClinics: null, defaultStaff: null, defaultView: null };
    }
    const cacheFilters = getCacheFilters(fullKey ?? null);

    // Synchronous migration read: when 2.1 first activates for a region-based practice,
    // the clinic entry is empty but the region entry has saved filters. Return region data
    // now so filters initialise with persisted values before any init effects run.
    if (cacheFilters === null && fullKey?.startsWith("clinic:") && currentRegion?.id) {
      const regionFilters = getCacheFilters(`region:${currentRegion.id}`);
      if (regionFilters) {
        return {
          defaultClinics: regionFilters.clinics ?? null,
          defaultStaff: regionFilters.staff ?? null,
          defaultView: regionFilters.view ?? null
        };
      }
    }

    return {
      defaultClinics: cacheFilters?.clinics ?? null,
      defaultStaff: cacheFilters?.staff ?? null,
      defaultView: cacheFilters?.view ?? null
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storageVersion is a cache-buster for localStorage re-reads
  }, [disabled, fullKey, storageVersion, currentRegion?.id]);

  const updateStorage = useCallback(
    (key: string, values: any) => {
      if (disabled || !fullKey) return;
      const filters = getFilters();
      const currentFiltersForKey = getCacheFilters(fullKey);

      const newState = { ...filters };
      newState[fullKey] = {
        ...currentFiltersForKey,
        [key]: values
      };
      clinicFiltersStorage.updateData(newState);
      setStorageVersion((v) => v + 1);
    },
    [disabled, fullKey]
  );

  return {
    defaultClinics,
    defaultStaff,
    defaultView,
    updateStorage,
    contextKey: disabled ? "noop" : fullKey
  };
}
